// hooks/useIdleDetection.js
import { useState, useEffect, useRef } from 'react';

/**
 * Hook to detect user idle state
 * @param {number} timeoutMs - Idle timeout in milliseconds (default: 30 seconds)
 * @returns {Object} { isIdle, resetTimer, lastActivity }
 */
export function useIdleDetection(timeoutMs = 30000) {
  const [isIdle, setIsIdle] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const timerRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    
    setIsIdle(false);
    setLastActivity(Date.now());
    
    timerRef.current = setTimeout(() => {
      setIsIdle(true);
    }, timeoutMs);
  };

  useEffect(() => {
    // Events that indicate user activity
    const events = [
      'mousedown',
      'mousemove', 
      'keypress',
      'scroll',
      'touchstart',
      'touchmove',
      'click',
      'pointermove'
    ];
    
    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetTimer, { passive: true, capture: true });
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, resetTimer, { capture: true });
      });
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [timeoutMs]);

  return { 
    isIdle, 
    resetTimer,
    lastActivity,
    idleTimeoutMs: timeoutMs
  };
}
